import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type GroupInvitation = components['schemas']['GroupInvitationResource']

export function useGroupInvitations(groupId: string) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'invitations'],
    queryFn: async (): Promise<GroupInvitation[]> => {
      const result = await api.GET('/platform/groups/{group}/invitations', {
        params: { path: { group: groupId } },
      })
      if (!result.data) {
        throw new Error('GET /platform/groups/{group}/invitations returned no body')
      }
      return result.data.data
    },
  })
}

/** Resend the invite email. Reversible, so the panel fires it with no confirmation. */
export function useResendInvitation(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.POST('/platform/groups/{group}/invitations/{groupInvitation}/resend', {
        params: { path: { group: groupId, groupInvitation: invitationId } },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', groupId, 'invitations'],
      })
    },
  })
}

/** Revoke an invitation — the customer's only way in, hence the confirmation dialog above this. */
export function useRevokeInvitation(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.DELETE('/platform/groups/{group}/invitations/{groupInvitation}', {
        params: { path: { group: groupId, groupInvitation: invitationId } },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', groupId, 'invitations'],
      })
    },
  })
}
